import { KEY_LABEL_MAP } from "./UpdateHistoryModal.constants";
import { ACTION_TYPES_ENUM } from "../Transactions.constants";
import { checkIsStringEmpty } from "features/Common/modules/DataTypes/Strings.utils";

/**
 * Checks if a value is not null, undefined, or an empty string.
 *
 * @param {any} val
 * @returns {boolean}
 */
export const isValidValue = (val) =>
  val !== null && val !== undefined && !checkIsStringEmpty(String(val));

/**
 * Maps raw database/API keys (snake_case) to human-readable UI labels.
 *
 * If the key exists in the KEY_LABEL_MAP constant, it returns the matched label.
 * Otherwise, it splits the key by underscores, capitalizes each word, and
 * joins them with a space as a fallback formatting.
 *
 * @example
 * getFriendlyLabel("company_name") => "Company Name"
 * getFriendlyLabel("some_custom_field") => "Some Custom Field"
 *
 * @param {string} key - The raw field name/key to map.
 * @returns {string} The formatted user-friendly label.
 */
export const getFriendlyLabel = (key) => {
  return (
    KEY_LABEL_MAP[key] ||
    key
      .split("_")
      .map((word) => word?.charAt(0)?.toUpperCase() + word?.slice(1))
      .join(" ")
  );
};

/**
 * Extracts list of changed entries between oldValues and newValues that have valid printable values.
 *
 * @param {Object} oldValues
 * @param {Object} newValues
 * @returns {Array<string>} List of changed field keys.
 */
export const getChangedEntries = (oldValues, newValues) => {
  const allKeys = Array.from(
    new Set([...Object.keys(oldValues || {}), ...Object.keys(newValues || {})])
  );

  return allKeys.filter((key) => {
    const oldVal = oldValues?.[key];
    const newVal = newValues?.[key];
    const isDifferent =
      String(oldVal || "").trim() !== String(newVal || "").trim();
    const hasValue = isValidValue(oldVal) || isValidValue(newVal);
    return isDifferent && hasValue;
  });
};

/**
 * Extracts audit item fields (old/new values, timestamps, sorted actions, pdf URLs) from an audit history item.
 *
 * @param {Object} item
 * @returns {Object} Extracted data properties.
 */
export const getItemAuditData = (item) => {
  const oldValues = item?.old_values ?? item?.actions?.[0]?.old_values ?? {};
  const newValues = item?.new_values ?? item?.actions?.[0]?.new_values ?? {};
  const createdAt = item?.created_at || item?.actions?.[0]?.created_at;

  const actions = item?.actions || [];
  const sortedActions = [...actions].sort(
    (a, b) => a.action_type - b.action_type
  );

  const creditNoteUrl =
    typeof item?.credit_note === "string"
      ? item.credit_note
      : item?.credit_note?.url;
  const reissuedInvoiceUrl =
    typeof item?.reissued_invoice === "string"
      ? item.reissued_invoice
      : item?.reissued_invoice?.url;

  return {
    oldValues,
    newValues,
    createdAt,
    sortedActions,
    creditNoteUrl,
    reissuedInvoiceUrl,
  };
};

/**
 * Formats an ISO / standard date string into a user-friendly YYYY-MM-DD HH:MM:SS layout.
 * Returns the original string in case of parsing exceptions.
 *
 * @example
 * formatTimestamp("2026-08-21T10:08:19.853Z") => "2026-08-21 15:38:19" (depending on local timezone offset)
 *
 * @param {string} dateStr - The raw date string from the API.
 * @returns {string} The formatted datetime string.
 */
export const formatTimestamp = (dateStr) => {
  if (!dateStr) return "";
  try {
    const d = new Date(dateStr);
    const pad = (n) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(
      d.getDate()
    )} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
  } catch (e) {
    return dateStr;
  }
};

/**
 * Returns the CSS utility classes for the action tags in the history item headers.
 *
 * @param {number|string} actionType - The numeric action type from ACTION_TYPES_ENUM.
 * @returns {string} Tailind CSS classes for text, background, and borders.
 */
export const getTagClassName = (actionType) => {
  switch (actionType) {
    case ACTION_TYPES_ENUM.CREDIT_NOTE_RAISED:
      return "tw-text-amber-700 tw-bg-amber-50 tw-border tw-border-solid tw-border-amber-200";
    case ACTION_TYPES_ENUM.INVOICE_REISSUED:
      return "tw-text-[#493ab1] tw-bg-[#F5F3FF] tw-border tw-border-solid tw-border-[#D9D6F5]";
    default:
      return "tw-text-gray-600 tw-bg-gray-100 tw-border tw-border-solid tw-border-gray-200";
  }
};

/**
 * Generates a signed S3 URL for a given raw URL/path and opens it in a new tab.
 * Avoids Access Denied errors for private S3 PDF buckets.
 *
 * @param {string} rawUrl - The raw file URL or S3 key.
 */
export const openPdfUrl = async (rawUrl) => {
  if (!rawUrl) return;
  try {
    let key = rawUrl;
    if (
      typeof rawUrl === "string" &&
      (rawUrl.startsWith("http://") || rawUrl.startsWith("https://"))
    ) {
      const parsedUrl = new URL(rawUrl);
      key = parsedUrl.pathname.startsWith("/")
        ? parsedUrl.pathname.substring(1)
        : parsedUrl.pathname;
    }
    const { getS3SignedUrl } = await import("utils/Utils");
    const signedUrl = await getS3SignedUrl(key);
    if (signedUrl) {
      window.open(signedUrl, "_blank");
      return;
    }
  } catch (e) {
    console.error("Failed to generate signed S3 URL:", e);
  }
  window.open(rawUrl, "_blank");
};
